import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  search,
  searchCategory,
  searchCuratedCategory
} from "../../../lib/api/search";
import {
  requestProducts,
  receiveProducts,
  moreProductsLoaded,
  previousProductsLoaded,
  loadingPreviousProducts,
  loadingMoreProducts,
  restoreOldSelectedFacets,
  updatePersonalisationParams,
  setAdditionalQueryParams,
  updateAdditionalQueryParams,
  updateMultiFacet,
  dismissFloorFilter,
  updateSortFacet,
  clearSelections,
  setNewSearch,
  clearNewSearch,
  refreshAdvertisementBeacons,
  clearOnViewBeacon,
  clearOnLoadBeacon,
  updateImageGalleryOnboardDismissed as updateImageGalleryOnboardDismissedAction
} from "./actionCreators";
import { setImageGalleryOnboardDismissedFromCookie } from "./imageGalleryOnboardDismissed";
import { isLargeMediumViewPort } from "./viewportHelper";

import logger from "../../../lib/logger";
import {
  getPageSize,
  getPaginationData,
  getAdditionalQueryParams,
  getAdvertisementsAnalytics
} from "./selectors";
import { getLocation } from "../router/selectors";
import {
  getSearchApiDefaults,
  getNorthAmericanRegion
} from "../config/selectors";
import config from "../../../config/common";
import applyFacets from "./facets/apply";
import { updateLocationQuery } from "../router/actions";
import {
  analyticsLoadMore,
  analyticsLoadPrevious,
  analyticsAddPendingRefinementsToggled,
  analyticsAddPendingSortRefinement,
  applyAndRemovePendingEvents
} from "../analytics/actions";
import { clearError, setError } from "../error";
import { ERROR_ORIGIN } from "../../../components/ErrorAlert/constants";
import { CATEGORY_PAGE } from "../page/constants";
import clientSideRedirect from "../../../client/clientSideRedirect";
import getPageNumberFromQuery from "./getPageNumberFromQuery";

export { default as clear } from "./facets/clear";
export { default as clearAlwaysApply } from "./facets/clearAlwaysApply";
export { default as clearAll } from "./facets/clearAll";
export { default as selectAll } from "./facets/selectAll";
export { default as applyFacets } from "./facets/apply";
export { default as done } from "./facets/done";
export { undoSearch } from "./undo";
export {
  updateRangeFacet,
  setNextPriceRangeToCurrent,
  changeRangeFacet,
  applyRangeFacet,
  clearRangeFacet
} from "./range/actions";
import { getAdvertisingQueryParams } from "./getAdvertisingQueryParams";
import { getCategoryTreatments } from "../category/selectors";
import { getPageName } from "../page/selectors";
import { clearCuratedCategoryTreatments } from "../category";

const pageSize = config.api.product.search.pageSize;

export {
  requestProducts,
  receiveProducts,
  moreProductsLoaded,
  previousProductsLoaded,
  loadingPreviousProducts,
  loadingMoreProducts,
  restoreOldSelectedFacets,
  updatePersonalisationParams,
  setAdditionalQueryParams,
  updateAdditionalQueryParams,
  updateMultiFacet,
  dismissFloorFilter,
  clearSelections,
  setNewSearch,
  clearNewSearch,
  refreshAdvertisementBeacons
};

export const updateImageGalleryOnboardDismissed = imageGalleryOnboardDismissed => {
  setImageGalleryOnboardDismissedFromCookie(imageGalleryOnboardDismissed);

  return updateImageGalleryOnboardDismissedAction(imageGalleryOnboardDismissed);
};

// eslint-disable-next-line complexity
export const getSearchActionCreator = ({
  state,
  refine,
  sort,
  dispatch,
  useCuratedSearchFallback
}) => {
  const pageName = getPageName(state);
  const isCategory = pageName === CATEGORY_PAGE;

  if (!isCategory) {
    return search;
  }

  const hasTreatments = getCategoryTreatments(state);

  const curatedSearchWrapper = useCuratedSearchFallback
    ? (...args) =>
        searchCuratedCategory.apply(this, args).catch(error => {
          error.message = `Curated Search failed for cid ${args[0]?.cid}, falling back to regular search`;

          logger.error(error);

          dispatch(clearCuratedCategoryTreatments());

          return searchCategory.apply(this, args);
        })
    : searchCuratedCategory;

  return hasTreatments && !refine && !sort
    ? curatedSearchWrapper
    : searchCategory;
};

export const getSearchAction = (state, payload) => {
  const newPayload = {
    ...payload
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    newPayload.region = northAmericanRegion;
  }

  return getSearchActionCreator({
    state,
    refine: payload.refine,
    sort: payload.sort
  })(newPayload);
};

const getFetchAction = ({ page: { name: pageName } }) =>
  pageName === CATEGORY_PAGE ? fetchCategory() : fetchSearch();

const reapplyFacets = () => dispatch => {
  dispatch(restoreOldSelectedFacets());
  dispatch(applyFacets());
};
export const sendPlacementOnViewBeacon = () => (dispatch, getState) => {
  const state = getState();
  const advertisementsAnalytics = getAdvertisementsAnalytics(state);
  const onViewBeacon = advertisementsAnalytics?.placementBeacons?.onViewBeacon;

  if (onViewBeacon) {
    navigator.sendBeacon(onViewBeacon);
    dispatch(clearOnViewBeacon());
  }
};

export const sendPlacementOnLoadBeacon = () => (dispatch, getState) => {
  const state = getState();
  const advertisementsAnalytics = getAdvertisementsAnalytics(state);
  const onLoadBeacon = advertisementsAnalytics?.placementBeacons?.onLoadBeacon;

  if (onLoadBeacon) {
    navigator.sendBeacon(onLoadBeacon);
    dispatch(clearOnLoadBeacon());
  }
};

export function retrySearch({ actionType, forceReapplyFacets } = {}) {
  return (dispatch, getState) => {
    switch (actionType) {
      case previousProductsLoaded.type:
        dispatch(loadPrevious());
        break;
      case moreProductsLoaded.type:
        dispatch(loadMore());
        break;
      default:
        forceReapplyFacets
          ? dispatch(reapplyFacets())
          : dispatch(getFetchAction(getState()));
        break;
    }
  };
}

const runLoadPrevious = ({ dispatch, getState }) => {
  const state = getState();
  const location = getLocation(state);
  const pagination = getPaginationData(state);
  const page = pagination.page.previous;
  const queryLabel =
    location.query.q || location.query.cid || "current listing";
  if (pagination.offset.previous !== null) {
    dispatch(loadingPreviousProducts());

    const queryObject = getQueryObject({ state }, pagination.offset.previous);

    return getSearchAction(state, queryObject)
      .then(response => {
        dispatch(clearError());
        dispatch(previousProductsLoaded({ ...response, page }));
        dispatch(analyticsLoadPrevious({ response, page }));
      })
      .catch(error => {
        error.message = `${error.message} loadPrevious for ${queryLabel} failed`;
        logger.error(error);
        dispatch(
          setError({
            message: error.message,
            actionType: previousProductsLoaded.type,
            errorOrigin: ERROR_ORIGIN.search
          })
        );
        dispatch(previousProductsLoaded(error));
      });
  }
};

export const loadPreviousAsync = createAsyncThunk(
  "search/loadPrevious",
  async (_, { dispatch, getState }) => runLoadPrevious({ dispatch, getState })
);

export function loadPrevious() {
  return (dispatch, getState) => runLoadPrevious({ dispatch, getState });
}

const runLoadMore = ({ dispatch, getState }) => {
  const state = getState();
  const { query } = getLocation(state);
  const pagination = getPaginationData(state);
  const page = pagination.page.next;
  const queryLabel = query.q || query.cid || "current listing";

  if (pagination.offset.next) {
    const updatedQuery = {
      ...query,
      page
    };
    dispatch(loadingMoreProducts());
    dispatch(updateLocationQuery({ query: updatedQuery }));

    const queryObject = getQueryObject({ state }, pagination.offset.next);

    return getSearchAction(state, queryObject)
      .then(response => {
        dispatch(clearError());
        dispatch(moreProductsLoaded({ ...response, page }));
        dispatch(analyticsLoadMore({ response, page }));
      })
      .catch(error => {
        error.message = `${error.message} loadMore for ${queryLabel} failed`;
        logger.error(error);
        dispatch(
          setError({
            message: error.message,
            actionType: moreProductsLoaded.type,
            errorOrigin: ERROR_ORIGIN.search
          })
        );
        dispatch(moreProductsLoaded(error));
      });
  }
};

export const loadMoreAsync = createAsyncThunk(
  "search/loadMore",
  async (_, { dispatch, getState }) => runLoadMore({ dispatch, getState })
);

export function loadMore() {
  return (dispatch, getState) => runLoadMore({ dispatch, getState });
}

const getQueryObject = ({ state, isNewPage }, paginationOffset = null) => {
  const { query } = getLocation(state);
  const limit = getPageSize(state);

  const queryObject = {
    ...query,
    ...getSearchApiDefaults(state, isNewPage),
    ...getAdvertisingQueryParams(state),
    offset:
      paginationOffset !== null
        ? paginationOffset
        : pageSize * (getPageNumberFromQuery(query) - 1),
    limit
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    queryObject.region = northAmericanRegion;
  }

  return queryObject;
};

const runFetchProduct = ({
  isCategory,
  uuid,
  xSiteOriginFromServerRequest,
  isNewPage,
  dispatch,
  getState
}) => {
  const state = getState();
  const query = getQueryObject({ state, isNewPage });

  dispatch(requestProducts({ query, isNewPage }));
  const searchFunction = getSearchActionCreator({
    state,
    refine: query.refine,
    sort: query.sort,
    dispatch,
    useCuratedSearchFallback: true
  });

  const commonThings = async response => {
    if (isNewPage && (await clientSideRedirect({ response, state }))) {
      return null;
    }

    dispatch(clearError());

    await dispatch(
      receiveProducts({
        ...response,
        query,
        isNewPage
      })
    );

    dispatch(applyAndRemovePendingEvents());
  };

  return searchFunction(query, uuid, xSiteOriginFromServerRequest)
    .then(response => {
      const { products } = response;

      const additionalQueryParams = getAdditionalQueryParams(state);
      const effectiveRefine = query.refine || additionalQueryParams?.refine;
      const floorExists = !!effectiveRefine?.includes("floor:");

      if (!isCategory && products?.length === 0 && floorExists) {
        return searchFunction(
          query,
          uuid,
          xSiteOriginFromServerRequest,
          true
        ).then(response => commonThings(response));
      } else {
        return commonThings(response);
      }
    })
    .catch(error => {
      if (isNewPage) {
        dispatch(receiveProducts(error));
        throw error;
      }
      error.message = `${
        isCategory
          ? `${error.message} fetchCategory failed`
          : `${error.message} fetchSearch for ${query.q} failed`
      }`;
      logger.error(error);
      dispatch(
        setError({
          message: error.message,
          actionType: receiveProducts.type,
          errorOrigin: ERROR_ORIGIN.search
        })
      );

      return dispatch(receiveProducts(error));
    });
};

const fetchProduct = (
  isCategory,
  uuid,
  xSiteOriginFromServerRequest,
  isNewPage
) => (dispatch, getState) =>
  runFetchProduct({
    isCategory,
    uuid,
    xSiteOriginFromServerRequest,
    isNewPage,
    dispatch,
    getState
  });

export const fetchSearchAsync = createAsyncThunk(
  "search/fetchSearch",
  async (
    { uuid, xSiteOriginFromServerRequest, isNewPage } = {},
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      return await runFetchProduct({
        isCategory: false,
        uuid,
        xSiteOriginFromServerRequest,
        isNewPage,
        dispatch,
        getState
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCategoryAsync = createAsyncThunk(
  "search/fetchCategory",
  async (
    { uuid, xSiteOriginFromServerRequest, isNewPage } = {},
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      return await runFetchProduct({
        isCategory: true,
        uuid,
        xSiteOriginFromServerRequest,
        isNewPage,
        dispatch,
        getState
      });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const fetchSearch = fetchProduct.bind(null, false);
export const fetchCategory = fetchProduct.bind(null, true);
export function changeMultiFacet(toggledFacets, meta) {
  return dispatch => {
    dispatch(updateMultiFacet(toggledFacets, meta));
  };
}

export const dismissFloorDeselections = (dispatch, normalized) => {
  normalized
    .filter(n => n.facetId === "floor" && n.isSelected === false)
    .forEach(deselection => {
      dispatch(dismissFloorFilter(String(deselection.valueId)));
    });
};

export function changeMultiFacetAndApplyIfLargeMedium(toggledFacets) {
  return (dispatch, getState) => {
    const state = getState();
    const normalized = toggledFacets.map(
      ({ facet, facetValue, isSelected }) => ({
        facetId: facet.id,
        valueId: facetValue.id,
        isSelected
      })
    );

    const isMobile = !isLargeMediumViewPort(state);
    const meta = isMobile ? { skipDebounce: true } : {};

    dismissFloorDeselections(dispatch, normalized);

    dispatch(updateMultiFacet(normalized, meta));
    dispatch(analyticsAddPendingRefinementsToggled(toggledFacets));
  };
}

export const changeSort = ({ type, name }) => dispatch => {
  dispatch(updateSortFacet(type));
  dispatch(analyticsAddPendingSortRefinement({ type, name }));
  dispatch(applyFacets());
};
